import type { AppEvent, DerivedState, TimerState } from '@aj/core';
import type { CoachState } from '@aj/coach';

import { EVENTS, META, clearAll, readAll, readOne, writeMany, writeOne } from './idb.js';

/**
 * Local storage. This is the primary operational database — the phone is the
 * source of truth (spec §26), and the cloud is a locker.
 *
 * Two stores, and the split matters:
 *
 *   events — append-only, synced, the ONLY truth.
 *   meta   — device-local scratch that must never sync: the derived-state
 *            checkpoint (a rebuildable cache) and the running timer.
 *
 * Putting timer ticks in `events` would write thousands of rows a day and
 * destroy every property the log has, so it lives here instead.
 */

/** One place listing everything device-local, and its type. */
export type MetaValues = {
  checkpoint: DerivedState;
  timer: TimerState | null;
  deviceId: string;
  /** What the coach has taught and where a flow got to.
   *
   *  Here rather than in the event log deliberately: the journal is the
   *  student's RECORD — what Khata shows and what export hands them — and a
   *  dismissed tip is not evidence of anything. Being device-local means a
   *  new phone re-runs onboarding, which is a fair trade and arguably right;
   *  if that should follow the user later, it moves to a synced store when
   *  sync exists. */
  coach: CoachState;
};

export type MetaKey = keyof MetaValues;
type MetaRow<K extends MetaKey = MetaKey> = { key: K; value: MetaValues[K] };

export async function readMeta<K extends MetaKey>(key: K): Promise<MetaValues[K] | undefined> {
  const row = await readOne<MetaRow<K>>(META, key);
  return row?.value;
}

export async function writeMeta<K extends MetaKey>(key: K, value: MetaValues[K]): Promise<void> {
  await writeOne(META, { key, value } satisfies MetaRow<K>);
}

export async function allEvents(): Promise<AppEvent[]> {
  // ULID primary keys sort by time, so key order is log order.
  return readAll<AppEvent>(EVENTS);
}

/**
 * Append events, then tell everyone who cares — this tab's subscribers
 * directly, other tabs over a BroadcastChannel.
 *
 * We are the only writer, so we always know the exact delta. That is why this
 * needs no generic live-query engine: listeners receive the new events rather
 * than a signal to go re-read the entire log from disk.
 */
export async function appendEvents(events: readonly AppEvent[]): Promise<void> {
  if (events.length === 0) return;
  await writeMany(EVENTS, events);
  publish(events);
}

/** Wipe local data. Used by "delete my data" and by import-replace. */
export async function resetLocal(): Promise<void> {
  await clearAll([EVENTS, META]);
  channel?.postMessage({ kind: 'reset' });
  for (const listener of listeners) listener({ kind: 'reset' });
}

// ---------------------------------------------------------------------------
// Change notification
// ---------------------------------------------------------------------------

export type LogChange =
  /** These events were just written. Already sorted, may overlap what you hold. */
  | { kind: 'appended'; events: readonly AppEvent[] }
  /** Everything is gone. Reload from scratch. */
  | { kind: 'reset' };

type Listener = (change: LogChange) => void;

const listeners = new Set<Listener>();

/** Cross-tab fan-out. Absent in older browsers and in tests — everything
 *  still works, you just don't see another tab's writes until reload. */
const channel: BroadcastChannel | null =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('agrojatra:log');

if (channel) {
  channel.onmessage = (message: MessageEvent<LogChange>) => {
    for (const listener of listeners) listener(message.data);
  };
}

function publish(events: readonly AppEvent[]): void {
  const change: LogChange = { kind: 'appended', events };
  // AppEvent is plain JSON, so it survives structured clone as-is.
  channel?.postMessage(change);
  for (const listener of listeners) listener(change);
}

export function subscribeToLog(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
